var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { comparer } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import ReactSelect from "react-select";
import { useFormContext } from "./Form";
import { StaticMultipleValues } from "./StaticInput";
import { useTranslation } from "./hooks/useTranslation";
import { DEFAULT_ID, withFormWrapper } from "./withFormWrapper";
export var bootstrapStyle = {
    control: function (styles) { return (__assign(__assign({}, styles), { minHeight: "34px" })); },
    clearIndicator: function (styles) { return (__assign(__assign({}, styles), { padding: "2px 8px" })); },
    dropdownIndicator: function (styles) { return (__assign(__assign({}, styles), { padding: "2px 8px" })); },
    loadingIndicator: function (styles) { return (__assign(__assign({}, styles), { padding: "2px 8px" })); },
    menu: function (styles) { return (__assign(__assign({}, styles), { zIndex: 3 })); },
    option: function (styles) { return (__assign(__assign({}, styles), { minHeight: "29px", height: "inherit" })); },
};
var RawSelect = observer(function (_a) {
    var options = _a.options, components = _a.components, name = _a.name, onChange = _a.onChange, _b = _a.multiple, multiple = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.autoFocus, autoFocus = _d === void 0 ? false : _d, _e = _a.loading, loading = _e === void 0 ? false : _e, _f = _a.formatter, formatter = _f === void 0 ? function (option) { return option.label; } : _f, placeholder = _a.placeholder, _g = _a.uniqueId, uniqueId = _g === void 0 ? DEFAULT_ID : _g, label = _a.label, staticFormatter = _a.staticFormatter, _h = _a.isClearable, isClearable = _h === void 0 ? true : _h, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId, ariaLabelledby = _a.ariaLabelledby;
    var t = useTranslation();
    if (placeholder === undefined) {
        placeholder = "".concat(t("Select"), "...");
    }
    var _j = useFormContext(), values = _j.values, formDisabled = _j.formDisabled, isStatic = _j.isStatic;
    var emptyValue = multiple ? [] : null;
    var selectionValue = values.get(name);
    var currentValue = React.useMemo(function () {
        if (multiple) {
            return selectionValue
                ? selectionValue.map(function (v) {
                    return options.find(function (o) { return comparer.structural(o.value, v); });
                })
                : emptyValue;
        }
        var value = options.find(function (o) {
            return comparer.structural(o.value, selectionValue);
        });
        return value !== undefined ? value : emptyValue;
    }, [multiple, selectionValue, options, emptyValue]);
    var getValueOfSelected = React.useCallback(function (selected) {
        if (selected === null || selected === undefined) {
            return emptyValue;
        }
        if (multiple) {
            return selected.map(function (o) { return o.value; });
        }
        return selected.value;
    }, [multiple, emptyValue]);
    var getLabelOfSelected = React.useCallback(function (selected) {
        if (selected === null || selected === undefined) {
            return emptyValue;
        }
        if (multiple) {
            return selected.map(function (o) { return o.label; });
        }
        return selected.label;
    }, [multiple, emptyValue]);
    var getLabelsFromValues = React.useCallback(function (value, options) {
        if (value === null || value === undefined) {
            return emptyValue;
        }
        if (multiple) {
            var selected_1 = options.filter(function (o) { return value.includes(o.value); });
            return selected_1.map(function (o) { return o.label; });
        }
        var selected = options.find(function (o) { return o.value === value; });
        return selected ? selected.label : emptyValue;
    }, [multiple, emptyValue]);
    var handleChange = React.useCallback(function (selected) {
        if (onChange) {
            var newValue = getValueOfSelected(selected);
            var oldValue = selectionValue;
            var newLabel = getLabelOfSelected(selected);
            var oldLabel = getLabelsFromValues(selectionValue, options);
            onChange(newValue, oldValue, newLabel, oldLabel);
        }
        values.set(name, getValueOfSelected(selected));
    }, [onChange, values, selectionValue, getValueOfSelected, name]);
    if (isStatic) {
        return (React.createElement(StaticMultipleValues, { label: label, name: name, staticFormatter: staticFormatter, isLoading: loading, options: options, multiple: multiple, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
    }
    return (React.createElement(ReactSelect, { "aria-labelledby": ariaLabelledby !== null && ariaLabelledby !== void 0 ? ariaLabelledby : "form-group-".concat(name, "-").concat(uniqueId), "aria-label": label, id: "form-group-".concat(name, "-").concat(uniqueId), components: components, styles: bootstrapStyle, isMulti: multiple, options: options, value: currentValue, onChange: handleChange, isClearable: isClearable, getOptionValue: JSON.stringify, getOptionLabel: formatter, className: "react-select", isDisabled: disabled || formDisabled || loading, classNamePrefix: "select", autoFocus: autoFocus, isLoading: loading, placeholder: placeholder, "data-id": dataId, "data-testid": dataTestId }));
});
export var Select = withFormWrapper({ WrappedComponent: RawSelect });
